import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClientAwareLink } from '@/components/ClientAwareLink';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StressTestLab } from '@/components/stress-test-lab';
import { CPUStressDashboard } from '@/components/CPUStressDashboard';
import SQLToolPage from '@/pages/SQLToolPage';
import { SuccessCriteria } from '@/components/SuccessCriteria';
import {
    Target,
    Zap,
    Activity,
    BarChart3
} from 'lucide-react';

export default function StabilizePage() {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-orange-100 rounded-lg">
                            <Target className="w-8 h-8 text-orange-600" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                Stabilize Phase
                            </h1>
                            <p className="text-gray-600 dark:text-gray-300">
                                Performance testing, stress testing, and system stabilization
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="success-criteria">Success Criteria</TabsTrigger>
                        <TabsTrigger value="sql-performance">SQL Performance</TabsTrigger>
                        <TabsTrigger value="analysis">Performance Analysis</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Load Testing */}
                            <Card>
                                <CardHeader className="pb-4">
                                    <CardTitle className="flex items-center gap-2 text-lg">
                                        <Zap className="w-5 h-5 text-yellow-600" />
                                        Load Testing
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Simulates expected peak user or transaction load to measure system behavior under normal and peak conditions.
                                    </p>
                                    <div className="flex justify-between text-sm">
                                        <span>Status</span>
                                        <span className="text-green-600">✓ Available</span>
                                    </div>
                                    </CardContent>
                                    </Card>
                                    {/* Spike Testing */}
                                    <Card>
                                        <CardHeader className="pb-4">
                                            <CardTitle className="flex items-center gap-2 text-lg">
                                                <Zap className="w-5 h-5 text-pink-600" />
                                                Spike Testing
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-sm text-gray-600 mb-4">
                                                Applies sudden increases or decreases in load to observe system recovery and stability.
                                            </p>
                                            <div className="flex justify-between text-sm">
                                                <span>Status</span>
                                                <span className="text-green-600">✓ Available</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                                      {/* Scalability Testing */}
                                                      <Card>
                                                        <CardHeader className="pb-4">
                                                          <CardTitle className="flex items-center gap-2 text-lg">
                                                            <Zap className="w-5 h-5 text-green-600" />
                                                            Scalability Testing
                                                          </CardTitle>
                                                        </CardHeader>
                                                        <CardContent>
                                                          <p className="text-sm text-gray-600 mb-4">
                                                            Measures how performance changes as load increases and determines the system’s ability to scale up or out.
                                                          </p>
                                                          <div className="flex justify-between text-sm">
                                                            <span>Status</span>
                                                            <span className="text-green-600">✓ Available</span>
                                                          </div>
                                                        </CardContent>
                                                      </Card>
                                                      {/* Stress Testing (CPU + SQL Server) */}
                                                      <ClientAwareLink href="/stabilize/stress">
                                                        <Card className="hover:shadow-lg hover:border-shiftleft-400 transition cursor-pointer">
                                                          <CardHeader className="pb-4">
                                                            <CardTitle className="flex items-center gap-2 text-lg">
                                                              <Zap className="w-5 h-5 text-red-600" />
                                                              Stress Testing
                                                            </CardTitle>
                                                          </CardHeader>
                                                          <CardContent>
                                                            <p className="text-sm text-gray-600 mb-2">
                                                              Includes both CPU and SQL Server stress scenarios:
                                                            </p>
                                                            <ul className="list-disc pl-5 text-sm text-gray-700 mb-2">
                                                              <li><b>CPU Stress:</b> Core Masking, Load Generation, Real-time Monitoring, Automated Recovery</li>
                                                              <li><b>SQL Server Stress:</b> TempDB Contention, Parameter Sniffing, Memory Pressure, Blocking Chains</li>
                                                            </ul>
                                                            <div className="flex justify-between text-sm">
                                                              <span>Status</span>
                                                              <span className="text-green-600">✓ Available</span>
                                                            </div>
                                                          </CardContent>
                                                        </Card>
                                                      </ClientAwareLink>
                                                    </div>
                                                    {/* Quick Actions */}
                                                    <Card>
                                                      <CardHeader>
                                                        <CardTitle className="flex items-center gap-2">
                                                          <Activity className="w-5 h-5 text-green-600" />
                                                          Quick Actions
                                                        </CardTitle>
                                                      </CardHeader>
                                                      <CardContent>
                                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                          <ClientAwareLink href="/stabilize/stress">
                                                            <button className="p-4 border border-gray-200 rounded-lg hover:bg-shiftleft-50 transition-colors text-left w-full text-left">
                                                              <div className="font-medium text-sm">Start SQL Stress Test</div>
                                                              <div className="text-xs text-gray-500 mt-1">Run comprehensive SQL Server stress testing</div>
                                                            </button>
                                                          </ClientAwareLink>
                                                          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                                                            <div className="font-medium text-sm">CPU Load Test</div>
                                                            <div className="text-xs text-gray-500 mt-1">Generate CPU load with core masking</div>
                                                          </button>
                                                          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                                                            <div className="font-medium text-sm">EDI Procedure Test</div>
                                                            <div className="text-xs text-gray-500 mt-1">Test customer EDI stored procedures</div>
                                                          </button>
                                                          <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                                                            <div className="font-medium text-sm">Performance Analysis</div>
                                                            <div className="text-xs text-gray-500 mt-1">Analyze system performance metrics</div>
                                                          </button>
                                                        </div>
                                                      </CardContent>
                                                    </Card>
                                                </TabsContent>
                                                <TabsContent value="success-criteria" className="space-y-4">
                                                    <SuccessCriteria />
                                                  </TabsContent>
                                                  <TabsContent value="sql-performance" className="space-y-4">
                                                    <CPUStressDashboard />
                                                  </TabsContent>
                                                  <TabsContent value="analysis" className="space-y-4">
                                                    <StressTestLab />
                                                  </TabsContent>
                                                </Tabs>
                                              </div>
                                              {/* Permanent Right Pane: Success Criteria */}
                                              <div className="hidden lg:block w-[420px] border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 overflow-y-auto">
                                                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Success Criteria</h2>
                                                <SuccessCriteria />
                                              </div>
        </div>
    );
}